import React from 'react';
import { AlertTriangle, XCircle, TrendingUp, Layers } from 'lucide-react';
import type { IntegrationHealth, JobsSummary } from '../../services/systemHealthService';

interface Props {
  healthUnavailable?: boolean;
  criticalCount24h: number | null;
  integrations: IntegrationHealth[];
  jobsSummary: JobsSummary | null;
  leadsToday: number | null;
  isLoading: boolean;
}

function countDegradedOrFailed(integrations: IntegrationHealth[]): { degraded: number; failed: number } {
  let degraded = 0;
  let failed = 0;
  for (const i of integrations) {
    const s = i.effective_status ?? i.status;
    if (s === 'degraded') degraded++;
    else if (s === 'failed') failed++;
  }
  return { degraded, failed };
}

interface StatCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  icon: React.ReactNode;
  variant: 'neutral' | 'danger' | 'warning' | 'success';
}

function StatCard({ title, value, subtitle, icon, variant }: StatCardProps) {
  const variantClasses = {
    neutral: 'bg-white border-gray-200 text-gray-700',
    danger: 'bg-red-50 border-red-200 text-red-700',
    warning: 'bg-amber-50 border-amber-200 text-amber-700',
    success: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  };

  const valueClasses = {
    neutral: 'text-gray-900',
    danger: 'text-red-700',
    warning: 'text-amber-700',
    success: 'text-emerald-700',
  };

  return (
    <div className={`rounded-xl border p-5 flex items-start gap-4 ${variantClasses[variant]}`}>
      <div className="mt-0.5 flex-shrink-0">{icon}</div>
      <div className="min-w-0">
        <p className="text-xs font-medium uppercase tracking-wide opacity-70 truncate">{title}</p>
        <p className={`text-3xl font-bold mt-1 ${valueClasses[variant]}`}>{value}</p>
        {subtitle && <p className="text-xs mt-1 opacity-60">{subtitle}</p>}
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 animate-pulse">
      <div className="h-3 bg-gray-200 rounded w-24 mb-3" />
      <div className="h-8 bg-gray-200 rounded w-12 mb-2" />
      <div className="h-3 bg-gray-200 rounded w-32" />
    </div>
  );
}

const SystemHealthOverview: React.FC<Props> = ({
  healthUnavailable = false,
  criticalCount24h,
  integrations,
  jobsSummary,
  leadsToday,
  isLoading,
}) => {
  const { degraded, failed } = countDegradedOrFailed(integrations);
  const integrationIssues = degraded + failed;

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => <SkeletonCard key={i} />)}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <StatCard
        title="Incidencias registradas (24h)"
        value={criticalCount24h ?? "Sin datos"}
        subtitle={criticalCount24h === null ? 'No se pudo consultar' : criticalCount24h === 0 ? 'Sin incidencias' : 'Requieren atenci\u00f3n'}
        icon={<XCircle className="w-6 h-6" />}
        variant={criticalCount24h === null ? 'neutral' : criticalCount24h > 0 ? 'danger' : 'success'}
      />

      <StatCard
        title="Integraciones con problemas"
        value={healthUnavailable || integrations.length === 0 ? "Sin datos" : integrationIssues}
        subtitle={
          healthUnavailable ? 'No se pudo consultar' : integrationIssues === 0
            ? 'Sin fallos registrados; consulta los servicios sin datos'
            : `${failed} fallidas · ${degraded} sin comprobación reciente`
        }
        icon={<Layers className="w-6 h-6" />}
        variant={failed > 0 ? 'danger' : degraded > 0 ? 'warning' : 'neutral'}
      />

      <StatCard
        title="Tareas automáticas fallidas"
        value={jobsSummary?.failed ?? "Sin datos"}
        subtitle={!jobsSummary ? 'No se pudo consultar' : jobsSummary.failed === 0 ? 'Sin tareas fallidas' : `${jobsSummary.pending} pendientes`}
        icon={<AlertTriangle className="w-6 h-6" />}
        variant={jobsSummary && jobsSummary.failed > 0 ? 'danger' : 'neutral'}
      />

      <StatCard
        title="Contactos comerciales de hoy"
        value={leadsToday ?? "Sin datos"}
        subtitle="Nuevas oportunidades registradas"
        icon={<TrendingUp className="w-6 h-6" />}
        variant={leadsToday !== null && leadsToday > 0 ? 'success' : 'neutral'}
      />
    </div>
  );
};

export default SystemHealthOverview;
